import React, { useState, useEffect, useRef } from 'react';
import '../Css/OurServices.css';
import { API_URL } from '../utils/Function';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCube,
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faInfoCircle,
  faHeading,
  faLink,
  faTag,
  faAlignLeft,
  faFileAlt,
  faIcons,
  faFolder,
  faListUl,
  faStar,
  faGift,
  faDollarSign,
  faMoneyBillWave,
  faCoins,
  faImages,
  faImage,
  faPhotoVideo,
  faEye,
  faProjectDiagram,
  faSortNumericDown,
  faToggleOn,
  faExclamationTriangle,
  faFolderOpen,
  faImages as faImagesSolid,
  faChevronLeft,
  faChevronRight,
  faPause,
  faPlay,
  faCogs,
  faIndustry,
  faBoxOpen,
  faSearch,
  faClock,
} from '@fortawesome/free-solid-svg-icons';

export default function OurServices() {
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [autoSlideEnabled, setAutoSlideEnabled] = useState({});
  const slideIntervals = useRef({});

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    tagline: '',
    description: '',
    short_description: '',
    icon: '',
    features: '',
    benefits: '',
    process_steps: '',
    technologies: '',
    pricing_type: 'custom',
    starting_price: '',
    currency: 'INR',
    duration: '',
    featured_image: null,
    gallery_images: [],
    project_id: '',
    service_category: '',
    target_industries: '',
    deliverables: '',
    is_featured: false,
    is_active: true,
    display_order: 0,
    meta_title: '',
    meta_description: ''
  });

  useEffect(() => {
    fetchServices();
    return () => {
      // Cleanup all intervals on component unmount
      Object.values(slideIntervals.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  // Auto-slide effect for each service
  useEffect(() => {
    services.forEach(service => {
      const images = getServiceImages(service);
      if (images.length > 1 && autoSlideEnabled[service.id]) {
        startAutoSlide(service.id, images.length);
      } else {
        stopAutoSlide(service.id);
      }
    });

    return () => {
      // Cleanup intervals when services change
      services.forEach(service => {
        stopAutoSlide(service.id);
      });
    };
  }, [services, autoSlideEnabled]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/service.php/get`);
      const data = await response.json();
      if (data.success) {
        setServices(data.services);
        const initialIndexes = {};
        const initialAutoSlide = {};
        data.services.forEach(service => {
          initialIndexes[service.id] = 0;
          initialAutoSlide[service.id] = true; // Enable auto-slide by default
        });
        setCurrentImageIndex(initialIndexes);
        setAutoSlideEnabled(initialAutoSlide);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      toast.error('Error fetching services');
    } finally {
      setLoading(false);
    }
  };

  const jsonArrayToString = (jsonArray) => {
    if (!jsonArray) return '';
    try {
      const array = JSON.parse(jsonArray);
      return Array.isArray(array) ? array.join(', ') : '';
    } catch (e) {
      return jsonArray || '';
    }
  };

  const stringToJsonArray = (string) => {
    if (!string) return '[]';
    const items = string.split(',').map(item => item.trim()).filter(item => item !== '');
    return JSON.stringify(items);
  };

  // Auto-slide functionality
  const startAutoSlide = (serviceId, totalImages) => {
    if (slideIntervals.current[serviceId]) {
      clearInterval(slideIntervals.current[serviceId]);
    }

    slideIntervals.current[serviceId] = setInterval(() => {
      setCurrentImageIndex(prev => ({
        ...prev,
        [serviceId]: (prev[serviceId] + 1) % totalImages
      }));
    }, 4000); // Change image every 4 seconds
  };

  const stopAutoSlide = (serviceId) => {
    if (slideIntervals.current[serviceId]) {
      clearInterval(slideIntervals.current[serviceId]);
      slideIntervals.current[serviceId] = null;
    }
  };

  const toggleAutoSlide = (serviceId) => {
    setAutoSlideEnabled(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Gallery slider functions
  const nextImage = (serviceId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[serviceId];
    
    // Temporarily disable auto-slide for manual navigation
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [serviceId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [serviceId]: (prev[serviceId] + 1) % totalImages
    }));

    // Re-enable auto-slide after 10 seconds if it was enabled
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [serviceId]: true
        }));
      }, 10000);
    }
  };

  const prevImage = (serviceId, totalImages) => {
    const wasAutoSlideEnabled = autoSlideEnabled[serviceId];
    
    // Temporarily disable auto-slide for manual navigation
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [serviceId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [serviceId]: (prev[serviceId] - 1 + totalImages) % totalImages
    }));

    // Re-enable auto-slide after 10 seconds if it was enabled
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [serviceId]: true
        }));
      }, 10000);
    }
  };

  const goToImage = (serviceId, index) => {
    const wasAutoSlideEnabled = autoSlideEnabled[serviceId];
    
    // Temporarily disable auto-slide for manual navigation
    if (wasAutoSlideEnabled) {
      setAutoSlideEnabled(prev => ({
        ...prev,
        [serviceId]: false
      }));
    }

    setCurrentImageIndex(prev => ({
      ...prev,
      [serviceId]: index
    }));

    // Re-enable auto-slide after 10 seconds if it was enabled
    if (wasAutoSlideEnabled) {
      setTimeout(() => {
        setAutoSlideEnabled(prev => ({
          ...prev,
          [serviceId]: true
        }));
      }, 10000);
    }
  };

  const getServiceImages = (service) => {
    const images = [];
    
    // Add gallery images if exist - handle both string and array formats
    if (service.gallery_images_urls) {
      let galleryUrls = service.gallery_images_urls;
      
      // If it's a string, try to parse it as JSON
      if (typeof galleryUrls === 'string') {
        try {
          galleryUrls = JSON.parse(galleryUrls);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
          // If parsing fails, treat it as a single URL string
          galleryUrls = [galleryUrls];
        }
      }
      
      // Ensure it's an array and add the images
      if (Array.isArray(galleryUrls)) {
        galleryUrls.forEach(url => {
          if (url && url.trim() !== '') {
            images.push({
              url: url,
              type: 'gallery'
            });
          }
        });
      }
    }
    
    // Add featured image if exists and no gallery images
    if (service.featured_image_url && images.length === 0) {
      images.push({
        url: service.featured_image_url,
        type: 'featured'
      });
    }
    
    return images;
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditMode(true);
      setSelectedService(service);
      
      let existingGallery = [];
      let existingFeaturedImage = null;

      if (service.gallery_images) {
        try {
          existingGallery = JSON.parse(service.gallery_images);
        } catch (e) {
          console.error('Error parsing gallery images:', e);
        }
      }

      if (service.featured_image_url) {
        existingFeaturedImage = service.featured_image_url;
      }

      setFormData({
        title: service.title || '',
        slug: service.slug || '',
        tagline: service.tagline || '',
        description: service.description || '',
        short_description: service.short_description || '',
        icon: service.icon || '',
        features: jsonArrayToString(service.features),
        benefits: jsonArrayToString(service.benefits),
        process_steps: jsonArrayToString(service.process_steps),
        technologies: jsonArrayToString(service.technologies),
        pricing_type: service.pricing_type || 'custom',
        starting_price: service.starting_price || '',
        currency: service.currency || 'INR',
        duration: service.duration || '',
        featured_image: null,
        gallery_images: [],
        project_id: service.project_id || '',
        service_category: service.service_category || '',
        target_industries: jsonArrayToString(service.target_industries),
        deliverables: jsonArrayToString(service.deliverables),
        is_featured: service.is_featured || false,
        is_active: service.is_active !== undefined ? service.is_active : true,
        display_order: service.display_order || 0,
        meta_title: service.meta_title || '',
        meta_description: service.meta_description || ''
      });

      // Set gallery previews
      const previews = existingGallery.map(filename => ({
        name: filename,
        url: `${API_URL}/uploads/services/${filename}`,
        isExisting: true
      }));
      setGalleryPreviews(previews);

      // Set featured image preview
      setFeaturedImagePreview(existingFeaturedImage);

    } else {
      setEditMode(false);
      setSelectedService(null);
      setFormData({
        title: '',
        slug: '',
        tagline: '',
        description: '',
        short_description: '',
        icon: '',
        features: '',
        benefits: '',
        process_steps: '',
        technologies: '',
        pricing_type: 'custom',
        starting_price: '',
        currency: 'INR',
        duration: '',
        featured_image: null,
        gallery_images: [],
        project_id: '',
        service_category: '',
        target_industries: '',
        deliverables: '',
        is_featured: false,
        is_active: true,
        display_order: 0,
        meta_title: '',
        meta_description: ''
      });
      setGalleryPreviews([]);
      setFeaturedImagePreview(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedService(null);
    setGalleryPreviews([]);
    setFeaturedImagePreview(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (type === 'file') {
      if (name === 'gallery_images') {
        const newFiles = Array.from(files);
        setFormData({ 
          ...formData, 
          gallery_images: [...formData.gallery_images, ...newFiles] 
        });
        
        // Create previews for new files
        newFiles.forEach(file => {
          const reader = new FileReader();
          reader.onload = (e) => {
            setGalleryPreviews(prev => [...prev, {
              name: file.name,
              url: e.target.result,
              isExisting: false,
              file: file
            }]);
          };
          reader.readAsDataURL(file);
        });
      } else if (name === 'featured_image') {
        setFormData({ ...formData, [name]: files[0] });
        
        // Create preview for featured image
        if (files[0]) {
          const reader = new FileReader();
          reader.onload = (e) => {
            setFeaturedImagePreview(e.target.result);
          };
          reader.readAsDataURL(files[0]);
        }
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const removeGalleryImage = (index) => {
    const imageToRemove = galleryPreviews[index];
    
    if (imageToRemove.isExisting) {
      // For existing images, mark for deletion in backend
      const updatedPreviews = galleryPreviews.filter((_, i) => i !== index);
      setGalleryPreviews(updatedPreviews);
      // Note: The actual deletion from server happens in the backend when new images are uploaded
    } else {
      // For new images, remove from both preview and formData
      const updatedPreviews = galleryPreviews.filter((_, i) => i !== index);
      setGalleryPreviews(updatedPreviews);
      
      // Find the corresponding file in formData.gallery_images
      const existingPreviewsCount = galleryPreviews.filter(p => p.isExisting).length;
      const fileIndex = index - existingPreviewsCount;
      
      if (fileIndex >= 0) {
        const updatedFiles = [...formData.gallery_images];
        updatedFiles.splice(fileIndex, 1);
        setFormData({ ...formData, gallery_images: updatedFiles });
      }
    }
  };

  const removeFeaturedImage = () => {
    setFormData({ ...formData, featured_image: null });
    setFeaturedImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formDataToSend = new FormData();
    
    const processedFormData = {
      ...formData,
      features: stringToJsonArray(formData.features),
      benefits: stringToJsonArray(formData.benefits),
      process_steps: stringToJsonArray(formData.process_steps),
      technologies: stringToJsonArray(formData.technologies),
      target_industries: stringToJsonArray(formData.target_industries),
      deliverables: stringToJsonArray(formData.deliverables)
    };
    
    // Append all form data except files
    Object.keys(processedFormData).forEach(key => {
      if (key !== 'featured_image' && key !== 'gallery_images') {
        if (processedFormData[key] !== null && processedFormData[key] !== '') {
          if (typeof processedFormData[key] === 'boolean') {
            formDataToSend.append(key, processedFormData[key] ? '1' : '0');
          } else {
            formDataToSend.append(key, processedFormData[key]);
          }
        }
      }
    });

    // Handle featured image
    if (processedFormData.featured_image) {
      formDataToSend.append('featured_image', processedFormData.featured_image);
    }

    // Handle gallery images - use the field name that matches your PHP backend
    if (processedFormData.gallery_images && processedFormData.gallery_images.length > 0) {
      processedFormData.gallery_images.forEach((file, index) => {
        formDataToSend.append('gallery_images[]', file);
      });
    }

    if (editMode && galleryPreviews.length > 0) {
      const existingImages = galleryPreviews
        .filter(preview => preview.isExisting)
        .map(preview => preview.name);
      
      if (existingImages.length > 0) {
        formDataToSend.append('existing_gallery_images', JSON.stringify(existingImages));
      }
    }

    try {
      let url, method;
      if (editMode) {
        url = `${API_URL}/service.php/put/${selectedService.id}`;
        method = 'POST';
      } else {
        url = `${API_URL}/service.php/post`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method: method,
        body: formDataToSend
      });

      const data = await response.json();
      if (data.success) {
        fetchServices();
        handleCloseModal();
        if (editMode) {
          toast.success('Service updated successfully!', { className: 'toast-edit' });
        } else {
          toast.success('Service added successfully!', { className: 'toast-add' });
        }
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('An error occurred while submitting the form', { className: 'toast-error' });
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/service.php/delete/${deleteId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchServices();
        setShowDeleteModal(false);
        setDeleteId(null);
        toast.warning('Service deleted successfully!', { className: 'toast-delete' });
      } else {
        toast.error(data.error || 'An error occurred', { className: 'toast-error' });
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      toast.error('An error occurred while deleting the service', { className: 'toast-error' });
    }
  };

  return (
    <div className="our-services-container">
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="our-services-header">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h1 className="our-services-heading">
                <FontAwesomeIcon icon={faCube} className="me-3" />
                Our Services
              </h1>
            </div>
            <div className="col-md-6 text-end">
              <button className="add-service-btn" onClick={() => handleOpenModal()}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add New Service
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-5">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : services.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FontAwesomeIcon icon={faFolderOpen} />
            </div>
            <p className="empty-state-text">No services found. Create your first service!</p>
          </div>
        ) : (
          <div className="row">
            {services.map((service) => {
              const images = getServiceImages(service);
              const currentIndex = currentImageIndex[service.id] || 0;
              const hasMultipleImages = images.length > 1;
              const isAutoSlideEnabled = autoSlideEnabled[service.id];
              
              return (
                <div key={service.id} className="col-lg-4 col-md-6 mb-4">
                  <div className="service-item-card">
                    {/* Image Slider */}
                    {images.length > 0 ? (
                      <div className="service-item-image">
                        <img 
                          src={`${API_URL}/${images[currentIndex].url}`} 
                          alt={service.title} 
                        />
                        
                        {/* Navigation arrows for multiple images */}
                        {hasMultipleImages && (
                          <>
                            <button 
                              className="slider-arrow slider-arrow-prev"
                              onClick={(e) => {
                                e.stopPropagation();
                                prevImage(service.id, images.length);
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronLeft} />
                            </button>
                            <button 
                              className="slider-arrow slider-arrow-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                nextImage(service.id, images.length);
                              }}
                            >
                              <FontAwesomeIcon icon={faChevronRight} />
                            </button>
                            
                            {/* Image counter */}
                            <div className="image-counter">
                              <FontAwesomeIcon icon={faImagesSolid} className="me-1" />
                              {currentIndex + 1} / {images.length}
                            </div>

                            {/* Auto-slide toggle button */}
                            {hasMultipleImages && (
                              <button 
                                className="slider-control"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAutoSlide(service.id);
                                }}
                                title={isAutoSlideEnabled ? "Pause auto-slide" : "Play auto-slide"}
                              >
                                <FontAwesomeIcon icon={isAutoSlideEnabled ? faPause : faPlay} />
                              </button>
                            )}
                            
                            {/* Dots indicator */}
                            <div className="slider-dots">
                              {images.map((_, index) => (
                                <button
                                  key={index}
                                  className={`dot ${index === currentIndex ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    goToImage(service.id, index);
                                  }}
                                />
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="service-item-image">
                        <div className="no-image-placeholder">
                          <FontAwesomeIcon icon={faImage} />
                          <span>No Image</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="service-item-body">
                      <h5 className="service-item-title">{service.title}</h5>
                      {service.tagline && <p className="service-item-tagline">{service.tagline}</p>}
                      <p className="service-item-description">
                        {service.short_description || service.description?.substring(0, 120) + '...'}
                      </p>
                      {service.service_category && (
                        <span className="service-item-badge">
                          <FontAwesomeIcon icon={faTag} className="me-1" />
                          {service.service_category}
                        </span>
                      )}
                    </div>
                    
                    <div className="service-item-footer">
                      <button 
                        className="btn btn-sm btn-outline-primary" 
                        onClick={() => handleOpenModal(service)}
                        title="Edit Service"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-danger" 
                        onClick={() => handleDeleteClick(service.id)}
                        title="Delete Service"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Service Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FontAwesomeIcon icon={editMode ? faEdit : faPlus} className="me-2" />
                  {editMode ? 'Edit Service' : 'Add New Service'}
                </h5>
                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit} id="serviceForm">
                  <div className="row">
                    {/* Basic Information Section */}
                    <div className="col-12 mb-4">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        Basic Information
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Title <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="title" 
                        value={formData.title} 
                        onChange={handleInputChange} 
                        placeholder="Enter service title"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faLink} className="me-1" />
                        Slug <span className="text-danger">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="slug" 
                        value={formData.slug} 
                        onChange={handleInputChange} 
                        placeholder="service-url-slug"
                        required 
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faTag} className="me-1" />
                        Tagline
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="tagline" 
                        value={formData.tagline} 
                        onChange={handleInputChange} 
                        placeholder="A catchy one-liner for your service"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faAlignLeft} className="me-1" />
                        Description <span className="text-danger">*</span>
                      </label>
                      <textarea 
                        className="form-control" 
                        name="description" 
                        value={formData.description} 
                        onChange={handleInputChange} 
                        rows="4" 
                        placeholder="Detailed description of your service"
                        required
                      ></textarea>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Short Description
                      </label>
                      <textarea 
                        className="form-control" 
                        name="short_description" 
                        value={formData.short_description} 
                        onChange={handleInputChange} 
                        rows="2"
                        placeholder="Brief summary for cards and previews"
                      ></textarea>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faIcons} className="me-1" />
                        Icon
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="icon" 
                        value={formData.icon} 
                        onChange={handleInputChange} 
                        placeholder="fa-code, fa-mobile, etc."
                      />
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFolder} className="me-1" />
                        Service Category
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="service_category" 
                        value={formData.service_category} 
                        onChange={handleInputChange}
                        placeholder="Web Development, Mobile Apps, etc."
                      />
                    </div>

                    {/* Media Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faImages} className="me-2" />
                        Media & Images
                      </h6>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faImage} className="me-1" />
                        Featured Image
                      </label>
                      <input 
                        type="file" 
                        className="form-control" 
                        name="featured_image" 
                        onChange={handleInputChange} 
                        accept="image/*"
                      />
                      
                      {featuredImagePreview && (
                        <div className="mt-3 position-relative">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Featured Image Preview:
                          </h6>
                          <div className="position-relative d-inline-block">
                            <img 
                              src={`${API_URL}/${featuredImagePreview}`} 
                              alt="Featured preview"
                              className="featured-image-preview"
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm position-absolute top-0 end-0"
                              onClick={removeFeaturedImage}
                              title="Remove image"
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faPhotoVideo} className="me-1" />
                        Gallery Images
                      </label>
                      <input 
                        type="file" 
                        className="form-control" 
                        name="gallery_images" 
                        onChange={handleInputChange} 
                        accept="image/*" 
                        multiple
                      />
                      
                      
                      {galleryPreviews.length > 0 && (
                        <div className="mt-3">
                          <h6 className="mb-2">
                            <FontAwesomeIcon icon={faEye} className="me-2" />
                            Gallery Previews:
                          </h6>
                          <div className="row">
                            {galleryPreviews.map((preview, index) => (
                              <div key={index} className="col-md-4 col-6 mb-2 position-relative">
                                <img 
                                  src={preview.url} 
                                  alt={`Gallery ${index + 1}`}
                                  className="gallery-image-preview"
                                />
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm position-absolute top-0 end-0"
                                  onClick={() => removeGalleryImage(index)}
                                  title="Remove image"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Features & Details Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faListUl} className="me-2" />
                        Features & Details
                      </h6>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faStar} className="me-1" />
                        Features
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="features" 
                        value={formData.features} 
                        onChange={handleInputChange} 
                        placeholder="Responsive Design, Fast Performance, SEO Optimized"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faGift} className="me-1" />
                        Benefits
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="benefits" 
                        value={formData.benefits} 
                        onChange={handleInputChange} 
                        placeholder="Increased Sales, Better User Experience, Cost Effective"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faCogs} className="me-1" />
                        Process Steps
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="process_steps" 
                        value={formData.process_steps} 
                        onChange={handleInputChange} 
                        placeholder="Consultation, Planning, Development, Testing, Deployment"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faSearch} className="me-1" />
                        Technologies
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="technologies" 
                        value={formData.technologies} 
                        onChange={handleInputChange} 
                        placeholder="React, Node.js, MongoDB, AWS"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faIndustry} className="me-1" />
                        Target Industries
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="target_industries" 
                        value={formData.target_industries} 
                        onChange={handleInputChange} 
                        placeholder="Healthcare, E-commerce, Education, Finance"
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faBoxOpen} className="me-1" />
                        Deliverables
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="deliverables" 
                        value={formData.deliverables} 
                        onChange={handleInputChange} 
                        placeholder="Source Code, Documentation, Deployment, Support"
                      />
                    </div>

                    {/* Pricing Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                        Pricing Information
                      </h6>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faTag} className="me-1" />
                        Pricing Type
                      </label>
                      <select className="form-select" name="pricing_type" value={formData.pricing_type} onChange={handleInputChange}>
                        <option value="custom">Custom Quote</option>
                        <option value="fixed">Fixed Price</option>
                        <option value="hourly">Hourly Rate</option>
                        <option value="project">Per Project</option>
                      </select>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
                        Starting Price
                      </label>
                      <input 
                        type="number" 
                        className="form-control" 
                        name="starting_price" 
                        value={formData.starting_price} 
                        onChange={handleInputChange}
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faCoins} className="me-1" />
                        Currency
                      </label>
                      <select className="form-select" name="currency" value={formData.currency} onChange={handleInputChange}>
                        <option value="INR">INR (₹)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                        <option value="GBP">GBP (£)</option>
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        Duration
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="duration" 
                        value={formData.duration} 
                        onChange={handleInputChange}
                        placeholder="2-4 weeks, 1 month, etc."
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faProjectDiagram} className="me-1" />
                        Project ID
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="project_id" 
                        value={formData.project_id} 
                        onChange={handleInputChange}
                        placeholder="PROJ-001"
                      />
                    </div>

                    {/* Settings Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faCogs} className="me-2" />
                        Settings & Display
                      </h6>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faSortNumericDown} className="me-1" />
                        Display Order
                      </label>
                      <input 
                        type="number" 
                        className="form-control" 
                        name="display_order" 
                        value={formData.display_order} 
                        onChange={handleInputChange}
                        min="0"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          name="is_featured" 
                          checked={formData.is_featured} 
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          <FontAwesomeIcon icon={faStar} className="me-1" />
                          Featured Service
                        </label>
                      </div>
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <div className="form-check form-switch mt-4">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          name="is_active" 
                          checked={formData.is_active} 
                          onChange={handleInputChange}
                        />
                        <label className="form-check-label">
                          <FontAwesomeIcon icon={faToggleOn} className="me-1" />
                          Active Service
                        </label>
                      </div>
                    </div>

                    {/* SEO Section */}
                    <div className="col-12 mb-4 mt-3">
                      <h6 className="text-primary fw-bold mb-3">
                        <FontAwesomeIcon icon={faSearch} className="me-2" />
                        SEO Information
                      </h6>
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faHeading} className="me-1" />
                        Meta Title
                      </label>
                      <input 
                        type="text" 
                        className="form-control" 
                        name="meta_title" 
                        value={formData.meta_title} 
                        onChange={handleInputChange}
                        placeholder="SEO title for search engines"
                      />
                    </div>
                    
                    <div className="col-md-12 mb-3">
                      <label className="form-label">
                        <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                        Meta Description
                      </label>
                      <textarea 
                        className="form-control" 
                        name="meta_description" 
                        value={formData.meta_description} 
                        onChange={handleInputChange}
                        rows="2"
                        placeholder="SEO description for search engines"
                      ></textarea>
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="submit" form="serviceForm" className="btn btn-primary">
                  <FontAwesomeIcon icon={editMode ? faSave : faPlus} className="me-1" />
                  {editMode ? 'Update Service' : 'Add Service'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                  Confirm Delete
                </h5>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this service? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  <FontAwesomeIcon icon={faTrash} className="me-1" />
                  Delete Service
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}